import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import './TutorialTooltip.css';

const TutorialTooltip = () => {
    const {
        isActive,
        isVisible,
        targetElement,
        currentStepConfig,
        currentStep,
        totalSteps,
        nextStep,
        prevStep,
        skipTutorial
    } = useTutorial();

    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [spotlightPosition, setSpotlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const tooltipRef = useRef(null);

    // Calculate tooltip and spotlight positions (viewport-relative for fixed positioning)
    useEffect(() => {
        if (!targetElement || !isVisible) return;

        const updatePositions = () => {
            const rect = targetElement.getBoundingClientRect();

            // Spotlight position (fixed to viewport)
            setSpotlightPosition({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });

            // Tooltip position based on configured position
            // Use dynamic width based on screen size
            const isMobile = window.innerWidth <= 768;
            const isSmallMobile = window.innerWidth <= 480;
            const tooltipWidth = isSmallMobile ? window.innerWidth - 24 : (isMobile ? window.innerWidth - 32 : 320);
            const tooltipHeight = isMobile ? 200 : 180;
            const spacing = isMobile ? 12 : 16;

            let tooltipTop = 0;
            let tooltipLeft = 0;

            // On mobile, prefer bottom or top positioning for better visibility
            const preferredPosition = isMobile && (currentStepConfig?.position === 'left' || currentStepConfig?.position === 'right')
                ? 'bottom'
                : currentStepConfig?.position;

            switch (preferredPosition) {
                case 'top':
                    tooltipTop = rect.top - tooltipHeight - spacing;
                    tooltipLeft = isMobile ? (window.innerWidth - tooltipWidth) / 2 : rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    break;
                case 'bottom':
                    tooltipTop = rect.bottom + spacing;
                    tooltipLeft = isMobile ? (window.innerWidth - tooltipWidth) / 2 : rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    break;
                case 'left':
                    tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                    tooltipLeft = rect.left - tooltipWidth - spacing;
                    break;
                case 'right':
                    tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                    tooltipLeft = rect.right + spacing;
                    break;
                default:
                    tooltipTop = rect.bottom + spacing;
                    tooltipLeft = isMobile ? (window.innerWidth - tooltipWidth) / 2 : rect.left + (rect.width / 2) - (tooltipWidth / 2);
            }

            // Ensure tooltip stays in viewport with mobile-friendly margins
            const margin = isSmallMobile ? 8 : (isMobile ? 12 : 16);
            tooltipLeft = Math.max(margin, Math.min(tooltipLeft, window.innerWidth - tooltipWidth - margin));
            tooltipTop = Math.max(margin, Math.min(tooltipTop, window.innerHeight - tooltipHeight - margin));

            // If tooltip would cover the target on mobile, move it below or above
            if (isMobile) {
                const tooltipBottom = tooltipTop + tooltipHeight;
                const targetBottom = rect.bottom;
                if (tooltipTop < targetBottom && tooltipBottom > rect.top) {
                    // Tooltip overlaps target, move to bottom
                    if (rect.bottom + spacing + tooltipHeight < window.innerHeight - margin) {
                        tooltipTop = rect.bottom + spacing;
                    } else {
                        // Move to top if there's space
                        tooltipTop = Math.max(margin, rect.top - tooltipHeight - spacing);
                    }
                }
            }

            setPosition({ top: tooltipTop, left: tooltipLeft });
        };

        updatePositions();

        // Update position during scroll animation
        const scrollInterval = setInterval(updatePositions, 50);

        window.addEventListener('resize', updatePositions);
        window.addEventListener('scroll', updatePositions, { passive: true });

        // Stop interval after scroll animation completes
        setTimeout(() => clearInterval(scrollInterval), 2000);

        return () => {
            clearInterval(scrollInterval);
            window.removeEventListener('resize', updatePositions);
            window.removeEventListener('scroll', updatePositions);
        };
    }, [targetElement, isVisible, currentStepConfig]);

    // Note: We no longer auto-advance on target click
    // Users must click "Next" to advance the tutorial
    // This allows them to actually interact with the highlighted elements

    if (!isActive || !isVisible || !targetElement || !currentStepConfig) {
        return null;
    }

    const progress = ((currentStep + 1) / totalSteps) * 100;

    return createPortal(
        <div className="tutorial-overlay">
            {/* Backdrop with hole for spotlight */}
            <div className="tutorial-backdrop">
                <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
                    <defs>
                        <mask id="spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            <rect
                                x={spotlightPosition.left - 8}
                                y={spotlightPosition.top - 8}
                                width={spotlightPosition.width + 16}
                                height={spotlightPosition.height + 16}
                                rx="12"
                                fill="black"
                            />
                        </mask>
                    </defs>
                    <rect
                        width="100%"
                        height="100%"
                        fill="rgba(0, 0, 0, 0.75)"
                        mask="url(#spotlight-mask)"
                    />
                </svg>
            </div>

            {/* Spotlight highlight ring */}
            <div
                className="tutorial-spotlight"
                style={{
                    top: spotlightPosition.top - 8,
                    left: spotlightPosition.left - 8,
                    width: spotlightPosition.width + 16,
                    height: spotlightPosition.height + 16
                }}
            />

            {/* Clickable zone over spotlight - allows clicks to pass through to target element */}
            <div
                className="tutorial-clickable-zone"
                style={{
                    position: 'absolute',
                    top: spotlightPosition.top - 8,
                    left: spotlightPosition.left - 8,
                    width: spotlightPosition.width + 16,
                    height: spotlightPosition.height + 16,
                    borderRadius: '12px',
                    zIndex: 2000002,
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                }}
                onClick={(e) => {
                    // Allow the click to reach the target element
                    if (targetElement) {
                        targetElement.click();
                    }
                }}
            />

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="tutorial-tooltip"
                style={{
                    top: position.top,
                    left: position.left
                }}
            >
                {/* Progress bar */}
                <div className="tutorial-progress">
                    <div className="tutorial-progress-bar" style={{ width: `${progress}%` }} />
                </div>

                {/* Step indicator */}
                <div className="tutorial-step-indicator">
                    Step {currentStep + 1} of {totalSteps}
                </div>

                {/* Content */}
                <div className="tutorial-content">
                    <h3 className="tutorial-title">{currentStepConfig.title}</h3>
                    <p className="tutorial-message">{currentStepConfig.message}</p>
                </div>

                {/* Actions */}
                <div className="tutorial-actions">
                    <button className="tutorial-skip" onClick={skipTutorial}>
                        Skip Tutorial
                    </button>
                    <div className="tutorial-nav">
                        {currentStep > 0 && (
                            <button className="tutorial-prev" onClick={prevStep}>
                                ← Back
                            </button>
                        )}
                        <button className="tutorial-next" onClick={nextStep}>
                            {currentStepConfig.isFinal ? 'Finish' : 'Next →'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TutorialTooltip;
